import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, CalendarPlus, Clock, X } from 'lucide-react';
import { usePlayerData } from './PlayerDataContext';
import { getPlannerMe } from '../../api';
import CalendarFeedLink from '../planner/CalendarFeedLink';

// The player's own schedule (Club Planner, Phase 4). Everything comes from GET /api/planner/me,
// which only ever returns what concerns this player from published weeks: their group's parts of
// a rotation, their own meeting slot, their own gym content. Today first (next item highlighted),
// then the coming days.

const DAYS_AHEAD = 13;
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONO = "'IBM Plex Mono', ui-monospace, monospace";

const pad = n => String(n).padStart(2, '0');
const localDate = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (s, n) => { const [y, m, d] = s.split('-').map(Number); return localDate(new Date(y, m - 1, d + n)); };
const fmtDay = s => { const [y, m, d] = s.split('-').map(Number); return `${DOW[new Date(y, m - 1, d).getDay()]} ${d} ${MON[m - 1]}`; };
const isoWeek = s => {
  const [y, m, d] = s.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  return Math.ceil(((t - Date.UTC(t.getUTCFullYear(), 0, 1)) / 86400000 + 1) / 7);
};
const nowHHMM = () => { const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };

function changedText(c) {
  if (!c) return null;
  if (c.kind === 'new') return 'New';
  return c.was_start ? `Moved · was ${c.was_start}` : 'Moved';
}

const VARIANT_TEXT = { played: 'If you played', not_played: "If you didn't play" };

// One item in the list. Tapping it opens ItemSheet with everything about it; the card itself keeps
// long notes to two lines.
function ItemCard({ item, next, onOpen }) {
  const dark = next;
  const sub = [item.end && item.start ? `until ${item.end}` : null, item.location].filter(Boolean);
  return (
    <button type="button" onClick={() => onOpen(item)} aria-label={`${item.title}${item.start ? `, ${item.start}` : ''}. Show details`} style={{
      display: 'flex', gap: 12, borderRadius: 12, padding: dark ? '10px 12px' : '9px 12px', width: '100%', boxSizing: 'border-box',
      background: dark ? '#15171a' : 'var(--bg-card)', color: dark ? '#fff' : 'var(--text-primary)',
      border: dark ? 'none' : '1px solid var(--border)', font: 'inherit', textAlign: 'left', cursor: 'pointer',
    }}>
      <span style={{ fontFamily: MONO, fontSize: 12, width: 44, flexShrink: 0, opacity: dark ? 0.85 : 1, color: dark ? '#fff' : 'var(--text-secondary)', paddingTop: dark ? 14 : 1 }}>
        {item.start || '—'}
      </span>
      <span aria-hidden="true" style={{ width: 4, borderRadius: 2, background: item.color, flexShrink: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        {dark && <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em', opacity: 0.8 }}>NEXT</span>}
        <span style={{ fontWeight: 600, fontSize: 15 }}>{item.title}</span>
        {(sub.length > 0 || !item.start) && (
          <span style={{ fontSize: 12, opacity: dark ? 0.85 : 1, color: dark ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            {item.location && <MapPin size={11} />}
            {!item.start ? 'Time to be confirmed' : sub.join(' · ')}
          </span>
        )}
        {item.note && <span style={{ fontSize: 12, color: dark ? '#fff' : 'var(--text-secondary)', opacity: dark ? 0.85 : 1 }}>{item.note}</span>}
        {item.topics.length > 0 && (
          <ul style={{ margin: '2px 0 0', paddingLeft: 16, fontSize: 12, color: dark ? '#fff' : 'var(--text-secondary)' }}>
            {item.topics.map(t => <li key={t}>{t}</li>)}
          </ul>
        )}
        {item.specific_points && (
          <span style={{
            fontSize: 12, marginTop: 2, padding: '4px 8px', borderRadius: 6, background: dark ? 'rgba(255,255,255,0.12)' : 'var(--bg-subtle)', color: dark ? '#fff' : 'var(--text-secondary)',
            whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {item.specific_points}
          </span>
        )}
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
          {item.optional && <Badge text="Optional" />}
          {item.variant && <Badge text={VARIANT_TEXT[item.variant]} />}
          {item.changed && <Badge text={changedText(item.changed)} warn />}
        </span>
      </div>
      <ChevronRight size={16} aria-hidden="true" style={{ alignSelf: 'center', flexShrink: 0, opacity: 0.5 }} />
    </button>
  );
}

// Everything about one item, as a sheet from the bottom of the screen: time, place, the notes
// staff wrote for the player (specific points), topics. Never the staff note: that is not in the
// API response at all (services/planner/playerView.js). Closes on ×, the backdrop or Escape.
function ItemSheet({ item, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, [onClose]);

  const time = item.start ? `${item.start}${item.end ? ` – ${item.end}` : ''}` : 'Time to be confirmed';
  const section = { display: 'flex', flexDirection: 'column', gap: 4 };
  const heading = { fontFamily: MONO, fontSize: 10, letterSpacing: '0.08em', color: 'var(--text-muted)' };
  return (
    <div role="presentation" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="item-sheet-title" onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', boxSizing: 'border-box',
          background: 'var(--bg-app)', color: 'var(--text-primary)', borderRadius: '16px 16px 0 0',
          padding: '16px 18px calc(20px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 14,
        }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span aria-hidden="true" style={{ width: 5, alignSelf: 'stretch', borderRadius: 3, background: item.color, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {item.category_label && <span style={heading}>{item.category_label.toUpperCase()}</span>}
            <h2 id="item-sheet-title" style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>{item.title}</h2>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 18, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Clock size={15} style={{ color: 'var(--text-muted)' }} />{fmtDay(item.date)} · {time}</span>
          {item.location && <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><MapPin size={15} style={{ color: 'var(--text-muted)' }} />{item.location}</span>}
        </div>

        {(item.optional || item.variant || item.changed) && (
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {item.optional && <Badge text="Optional" />}
            {item.variant && <Badge text={VARIANT_TEXT[item.variant]} />}
            {item.changed && <Badge text={changedText(item.changed)} warn />}
          </span>
        )}

        {item.note && <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{item.note}</div>}

        {item.specific_points && (
          <div style={section}>
            <span style={heading}>NOTES</span>
            <div style={{ fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap', padding: '10px 12px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              {item.specific_points}
            </div>
          </div>
        )}

        {item.topics.length > 0 && (
          <div style={section}>
            <span style={heading}>TOPICS</span>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.5 }}>
              {item.topics.map(t => <li key={t}>{t}</li>)}
            </ul>
          </div>
        )}

        {!item.specific_points && !item.note && !item.topics.length && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No extra notes for this item.</div>
        )}
      </div>
    </div>
  );
}

function Badge({ text, warn }) {
  if (!text) return null;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.03em', padding: '2px 6px', borderRadius: 4, background: warn ? '#f59e0b' : 'rgba(127,127,127,0.18)', color: warn ? '#15171a' : 'inherit' }}>
      {text}
    </span>
  );
}

export default function PlayerSchedule() {
  const navigate = useNavigate();
  const { sessionToken, playerRecord } = usePlayerData();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const today = localDate(new Date());
  const calendarRef = useRef(null);
  const [openItem, setOpenItem] = useState(null);
  const closeItem = useCallback(() => setOpenItem(null), []);

  useEffect(() => {
    if (!sessionToken) { setError('session'); return; }
    getPlannerMe(sessionToken, today, addDays(today, DAYS_AHEAD))
      .then(setData)
      .catch(e => setError(e.status === 401 ? 'session' : e.message));
  }, [sessionToken, today]);

  const todayDay = data?.days.find(d => d.date === today);
  const now = nowHHMM();
  const nextItem = todayDay?.items.find(i => i.start && (i.end || i.start) > now);
  const later = (data?.days || []).filter(d => d.date > today);

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-app)', paddingBottom: 28 }}>
      <div style={{ background: '#15171a', color: '#fff', padding: '14px 18px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <button type="button" onClick={() => navigate('/player/me')}
            style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'none', border: 'none', color: '#fff', opacity: 0.8, padding: 0, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', minHeight: 32 }}>
            <ChevronLeft size={16} /> Me
          </button>
          {sessionToken && (
            <button type="button" onClick={() => calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid rgba(255,255,255,0.45)', borderRadius: 16, color: '#fff', padding: '0 10px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', minHeight: 32 }}>
              <CalendarPlus size={14} /> Add to phone calendar
            </button>
          )}
        </div>
        <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em', opacity: 0.8 }}>MY SCHEDULE · WEEK {isoWeek(today)}</span>
        <span style={{ fontWeight: 800, fontSize: 26 }}>{fmtDay(today)}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {todayDay?.label.text && <span style={{ fontFamily: MONO, fontSize: 11, background: '#fff', color: '#15171a', padding: '2px 7px', borderRadius: 4 }}>{todayDay.label.text}</span>}
          {todayDay?.fixtures.map(f => (
            <span key={f.opponent} style={{ fontSize: 13, opacity: 0.9 }}>{f.opponent} ({f.home_away}){f.kickoff_time ? ` · ${f.kickoff_time}` : ''}</span>
          ))}
          {!todayDay?.fixtures.length && playerRecord?.squad_number && <span style={{ fontSize: 13, opacity: 0.9 }}>#{playerRecord.squad_number}</span>}
          {todayDay?.individual && <span style={{ fontFamily: MONO, fontSize: 11, border: '1px solid currentColor', padding: '1px 7px', borderRadius: 4 }}>YOUR OWN PROGRAMME</span>}
        </div>
      </div>

      <div style={{ padding: '12px 14px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {error === 'session' && <Empty>Your session has expired. Sign out and sign in again to see your schedule.</Empty>}
        {error && error !== 'session' && <Empty>Could not load your schedule.</Empty>}
        {!error && !data && <Empty>Loading…</Empty>}
        {data && !todayDay?.published && <Empty>Today's schedule hasn't been published yet.</Empty>}
        {data && todayDay?.published && !todayDay.items.length && <Empty>{todayDay.type === 'off' ? 'Day off.' : 'Nothing planned for you today.'}</Empty>}
        {todayDay?.items.map(item => <ItemCard key={item.id} item={item} next={item === nextItem} onOpen={setOpenItem} />)}
      </div>

      {later.length > 0 && (
        <div style={{ padding: '18px 14px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {later.map(d => (
            <section key={d.date} aria-label={fmtDay(d.date)}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtDay(d.date)}</h3>
                {d.label.text && <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--text-secondary)' }}>{d.label.text}</span>}
                {d.fixtures.map(f => <span key={f.opponent} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.opponent} ({f.home_away}){f.kickoff_time ? ` · ${f.kickoff_time}` : ''}</span>)}
                {d.individual && <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--text-secondary)' }}>YOUR OWN PROGRAMME</span>}
              </div>
              {!d.published && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{d.type === 'off' ? 'Day off' : 'Not published yet'}</div>}
              {d.published && !d.items.length && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{d.type === 'off' ? 'Day off' : 'Nothing planned for you'}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {d.items.map(item => <ItemCard key={item.id} item={item} onOpen={setOpenItem} />)}
              </div>
            </section>
          ))}
        </div>
      )}

      {sessionToken && (
        <div ref={calendarRef} style={{ padding: '22px 16px 0', scrollMarginBottom: 24 }}>
          <CalendarFeedLink token={sessionToken} audienceText="only your own items" />
        </div>
      )}

      {openItem && <ItemSheet item={openItem} onClose={closeItem} />}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ padding: '14px 12px', borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 14, color: 'var(--text-muted)' }}>{children}</div>;
}
